import React, { useState, useEffect } from 'react';
import Autosuggest, { SuggestionsFetchRequestedParams } from 'react-autosuggest';
import { Field, useFormikContext, FormikProps } from 'formik';
import { apiClient } from "../../../utils/ApiClient";
import '../../common/form/css/ClientInput.css';
import { Option } from "../../common/form/ElogSelect";
import User from "../../../models/User";

interface ClientInputProps {
    contacts?: Option[];
}

interface FieldRenderProps {
    field: {
        name: string;
        value: string;
        onChange: (e: React.ChangeEvent<any>) => void;
        onBlur: () => void;
        onFocus: () => void;
    };
}

interface Comment {
    id: number;
    comment: string;
}

const ClientInput: React.FC<ClientInputProps> = ({ contacts }: ClientInputProps) => {

    const formik = useFormikContext() as FormikProps<any>;

    const [value, setValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<Option[]>([]);
    const [clientContacts, setClientContacts] = useState<Option[]>([]);
    const [suggestedCheckers, setSuggestedCheckers] = useState<User[]>([]);
    const [comments, setComments] = useState<Comment[]>([]);
    const [comment, setComment] = useState<string>('');

    useEffect(() => {
        if (contacts) {
            //setClientContacts(contacts);
            fetchClientContacts(formik.values.client);
        }
    }, [contacts]);

    useEffect(() => {
        const fetchData = async () => {
            const firstContactId = formik.values.clientContact?.[0];
            if (firstContactId > 0) {
                try {
                    const response = await apiClient.get(`suggest/checker/${firstContactId}`);
                    const data = response.data;
                    setSuggestedCheckers(data);
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            } else {
                setSuggestedCheckers([]);
            }
        };

        if (formik.values?.clientContact?.length > 0) {
            fetchData();
        } else {
            setSuggestedCheckers([]);
        }
    }, [formik?.values?.clientContact, formik]);

    const fetchClients = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/client?search=${encodeURIComponent(inputValue)}`);
            const clients: Option[] = apiResponse.data.map((client: { id: number; name: string }) => ({
                label: client.name,
                value: client.id.toString(),
            }));
            fetchClientContacts(inputValue);
            setSuggestions(clients);
        } catch (error) {
            console.error('Fetch clients error:', error);
        }
    };

    const onSuggestionsFetchRequested = ({ value }: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchClients(value);
        } else {
            setSuggestions([]);
        }
    };

    const onSuggestionsClearRequested = () => {
        setSuggestions([]);
    };

    const onSuggestionSelected = (event: React.FormEvent<any>, { suggestion }: { suggestion: Option }) => {
        setValue(suggestion.label);
        fetchClientContacts(suggestion.label);
    };

    const renderSuggestion = (suggestion: Option) => (
        <div>
            {suggestion.label}
        </div>
    );

    const fetchClientContacts = async (selectedClientName: string) => {
        if (!selectedClientName) {
            setClientContacts([]);
            setComments([]);
            return;
        }

        try {
            const apiResponse = await apiClient.get(`client-contacts/client?clientName=${encodeURIComponent(selectedClientName)}`);
            const contacts: Option[] = apiResponse.data.map((contact: { id: number; name: string; comment?: string }) => ({
                label: contact.name,
                value: contact.id.toString(),
            }));
            const fetchedComments: Comment[] = apiResponse.data.map((contact: { id: number; comment?: string }) => ({
                comment: contact.comment || '',
                id: contact.id,
            }));

            const oldContacts = clientContacts.map(contact => `${contact.value}-${contact.label}`).join(',');
            const newContacts = contacts.map(contact => `${contact.value}-${contact.label}`).join(',');

            if (newContacts !== oldContacts) {

                setClientContacts(contacts);
                setComments(fetchedComments);

                const selectedContactValue = formik.values.clientContact?.[0];
                const isSelectedContactInNewContacts = contacts.some(contact => contact.value === selectedContactValue);

                if (contacts.length > 0 && !isSelectedContactInNewContacts) {
                    const newContact = contacts[0];
                    formik.setFieldValue('clientContact', [newContact.value]);
                    setComment(fetchedComments[0]?.comment || '');
                } else {
                    const contactComment = fetchedComments.find(contact => contact.id == selectedContactValue)?.comment;
                    setComment(contactComment || '');
                }
            } else {
                const selectedContactValue = formik.values.clientContact?.[0];
                const contactComment = fetchedComments.find(contact => contact.id == selectedContactValue)?.comment;
                setComment(contactComment || '');
            }
        } catch (error) {
            console.log('RESETTING');
            console.error('Fetch client contacts error:', error);
            setClientContacts([]);
            setComments([]);
            setComment('');
        }
    };

    const addClientContact = () => {
        const updatedClientContacts = [...(formik.values.clientContact || []), clientContacts?.[0]?.value].filter(Boolean) as string[];
        formik.setFieldValue('clientContact', updatedClientContacts);
    }

    const removeContact = (index: number) => {
        const updatedClientContacts = formik.values.clientContact?.filter((_: any, idx: number) => idx !== index) || [];
        formik.setFieldValue('clientContact', updatedClientContacts);
    };

    useEffect(() => {
        const newComment = comments.find((comment) => comment.id === parseInt(formik.values.clientContact?.[0] || '0'));
        setComment(newComment?.comment || '');
    }, [formik?.values?.clientContact, comments]);

    return (
        <>
            <tr>
                <th>Klient:</th>
                <td>
                    <Field
                        name="client"
                        render={({ field }: FieldRenderProps) => (
                            <Autosuggest
                                suggestions={suggestions}
                                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                                onSuggestionsClearRequested={onSuggestionsClearRequested}
                                onSuggestionSelected={onSuggestionSelected}
                                getSuggestionValue={(suggestion: Option) => suggestion.label}
                                renderSuggestion={renderSuggestion}
                                inputProps={{
                                    ...field,
                                    value: field.value || value,
                                    onChange: (_, { newValue }) => {
                                        setValue(newValue);
                                        const event = {
                                            target: {
                                                value: newValue,
                                                name: field.name,
                                            },
                                            persist: () => { },
                                        };
                                        field.onChange(event as React.ChangeEvent<HTMLInputElement>);
                                    },
                                }}
                                theme={{
                                    container: 'react-autosuggest__container',
                                    suggestionsContainer: suggestions.length > 0 ? 'react-autosuggest__suggestions-container visible' : 'react-autosuggest__suggestions-container',
                                    suggestionsList: 'react-autosuggest__suggestions-list',
                                    suggestion: 'react-autosuggest__suggestion',
                                    suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                                }}
                            />
                        )}
                    />
                </td>
            </tr>
            <tr>
                {formik?.values?.clientContact?.length < 2 &&
                <th>Kliendi kontakt*:</th>
                }
                {formik?.values?.clientContact?.length > 1 &&
                <th>Kliendi kontaktid*:</th>
                }
                <td>
                    {(formik?.values?.clientContact || []).map((contactId: string, index: number) => (
                        <Field
                            key={index}
                            name={`clientContact[${index}]`}
                            render={({field}: { field: any }) => (
                                contactId ? (
                                    <>
                                        <select
                                            {...field}
                                            value={contactId}
                                            onChange={(event) => {
                                                const selectedValue = event.target.value;
                                                const newClientContact = [...(formik?.values?.clientContact ?? [])];
                                                newClientContact[index] = selectedValue;
                                                formik.setFieldValue('clientContact', newClientContact);
                                            }}
                                        >
                                            <option value="" disabled>Vali kontaktisik</option>
                                            {clientContacts?.map((contactOption) => (
                                                <option key={contactOption?.value} value={contactOption?.value}>
                                                    {contactOption?.label}
                                                </option>
                                            ))}
                                        </select>
                                        {index > 0 && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 20,
                                                    color: 'red',
                                                    paddingLeft: '10px',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => removeContact(index)}
                                            >
                                                -
                                            </span>
                                        )}
                                        {index === 0 && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 20,
                                                    color: 'green',
                                                    paddingLeft: '10px',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={addClientContact}
                                            >
                                                +
                                            </span>
                                        )}
                                        <br/>
                                    </>
                                ) : null
                            )}
                        />
                    ))}
                    <div style={{fontWeight: 'bold', marginTop: '10px'}} id="client_contact_comment">
                        {comment}
                    </div>
                </td>
            </tr>
            {suggestedCheckers.length > 0 &&
            <tr>
                <th></th>
                <td>
                    Soovituslik check:<br />
                    {suggestedCheckers.map(user => (<p key={`check${user.id}`} style={{ fontWeight: 'bold' }}>{user.firstname} {user.lastname} ({user.orders_count})</p>))}
                </td>
            </tr>
            }
        </>
    );
};

export default ClientInput;